import { validate } from '~/schema';
import { validation as projectValidation } from '~/schema/project';
import { dedupeFilter, getEarliestProjectState, getProjectState, state2abbreviation, stringFieldSorter, stringSorter } from '~/utils';
import { calculateProjectCosts, sizeKwdc } from '~/utils/calculators';
import { DealStates, PricingType, Unit } from '~/constants';
import { useEffect, useMemo } from 'react';
import { usePromiseWatcher } from '~/hooks/index';
import { fetchPortfolio } from '~/api/portfolios';
import projectDisplayValues from '~/utils/displayValues/project';
import { usePermissions } from '~/requests/permissions/usePermissions';
import formConditions from '~/utils/formConditions';
import formConfig from '~/schema/project/config';
import { Project, ProjectBid } from '~/types/project';
import { normalizeToOverall } from '~/utils/calculators/costsFns';
import { PortfolioTotals, ProjectTotal } from '~/types/portfolio';

export const usePortfolioCalculatedValues = (portfolio: any, projects: any[]) => {
  const { permissions } = usePermissions();
  const projectBidCountMap = useMemo(() => {
    const bidCountMap = {} as any;
    projects?.forEach((project: Project) => {
      if (project?.parentId && project?.pendingBid) {
        bidCountMap[project.parentId]  = (bidCountMap[project.parentId] || 0) + 1;
      }
    });
    return bidCountMap;
  }, [projects]);

  const projectCount = projects?.filter((p: any) => p.portfolioId === portfolio.id)?.length;

  const parentProjects = projects?.filter((p: any) => !p.parentId);
  const childProjects = projects?.filter((p: any) => p.parentId && p.investorId);

  // Create a map of parent IDs to child projects
  const parentProjectChildProjectMap = childProjects?.reduce((acc: any, childProject: any) => {
    if (!acc[childProject.parentId]) {
      acc[childProject.parentId] = [];
    }
    acc[childProject.parentId].push(childProject);
    return acc;
  }, {});

  const projectBlobs = useMemo(
    () => {
      if (!projects?.length) {
        return [];
      }
      let blobs = projects
        .map((project: Project) => {
          const conditions = formConditions({
            data: project,
            config: formConfig,
            getTransformedData: false
          });
          
          const projectErrors = validate(project, projectValidation, {}, '', project, project, formConfig, conditions);
          const projectState = getProjectState(project, projectErrors, portfolio);
          const projectCosts = calculateProjectCosts(project, permissions);
          const displayValues = projectDisplayValues(project, projectCosts, permissions, projectState);
          return { project, projectErrors, projectState, projectCosts, projectDisplayValues: displayValues };
        });

      if (!portfolio.parentId) {
        // This is a portfolio still being bid on. Filter out child projects and set state accordingly
        const projectDealStateMap = blobs.filter((blob: any) => blob?.project?.parentId).reduce((acc: any, blob: any) => {
          if (blob?.projectState && blob?.project?.parentId) {
            if (!acc[blob.project.parentId]) {
              acc[blob.project.parentId] = blob.projectState.dealState;
            } else if (blob.projectState.dealState > acc[blob.project.parentId]) {
              acc[blob.project.parentId] = blob.projectState.dealState;
            }
          }
          return acc;
        }, {});
        blobs.forEach((blob: any) => {
          if (blob?.projectState && projectDealStateMap[blob?.project?.id] !== undefined) {
            blob.projectState.dealState = projectDealStateMap[blob.project.id];
          }
        });
      }
      blobs.sort(stringFieldSorter('project.name'));
      return blobs;
    },
    [projects]
  );

  const displayBlobs = useMemo(
    () => projectBlobs.filter((projectBlob: any) => !projectBlob.projectState.isDeclined),
    [projectBlobs]
  );

  const portfolioState = useMemo(
    () => getEarliestProjectState(projectBlobs, !permissions?.isAdmin && permissions?.hasPortfolioInvestorAccess),
    [projectBlobs, permissions]
  );

  const locations = useMemo(
    () => {
      return displayBlobs
        ?.map((projectBlob: any) => state2abbreviation(projectBlob.project.addressState) || 'unspecified')
        .filter(dedupeFilter)
        .sort(stringSorter)
        .join('; ') || <span>&nbsp;</span>;
    },
    [displayBlobs]
  );

  const totalBid = useMemo(
    () => {
      let allFlatUnits = undefined;
      let amount = 0;
      for (let projectBlob of displayBlobs) {
        const bid = projectBlob.project.acceptedBid ?? projectBlob.project.pendingBid;
        let isFlatUnit = bid?.unit ? (bid?.unit === Unit.TOTAL) : undefined;
        if (isFlatUnit === undefined) {
          continue;
        }
        if (allFlatUnits === undefined) {
          allFlatUnits = isFlatUnit;
        }
        if (isFlatUnit !== allFlatUnits) {
          return {unitLabel: 'Bids'};
        }
        if (isFlatUnit) {
          amount += bid?.amount as number;
        }
      }
      if (allFlatUnits === undefined) {
        return {};
      }
      if (allFlatUnits) {
        return {unitLabel: 'EPC Cost + Dev Fee', unit: Unit.TOTAL, amount};
      }
      return {unitLabel: 'Offtake Rate'};
    },
    [displayBlobs]
  );

  const portfolioTotals = useMemo(
    // This is used to show the totals that are certain for summed systems and costs in a portfolio
    // 'certain' means that this is not impacted by bidding until a bid is accepted (important for excel download)
    () => {
      const results: PortfolioTotals = {
        epcCost: 0,
        interconnection: 0,
        otherItc: 0,
        otherNonItc: 0,
        nrsFee: 0,
        totalCost: 0,
        sizeKwdc: 0,
        sizeMwdc: 0,
        projectTotals: []
      };
      
      for (let projectBlob of displayBlobs) {
        const { project, projectCosts } = projectBlob;
        const projectTotal = {} as ProjectTotal;
        const projectSizeKwdc = sizeKwdc(project);
        const projectSizeMwdc = projectSizeKwdc / 1000;

        projectTotal.projectId = project.id;

        if (project.dealState < DealStates.IN_DILIGENCE) {
          // Set portfolioTotals from the project directly (ignoring projectCosts data, which is either not available or an array of bids)
          const { 
            INTERCONNECTION: interconnection , 
            OTHER_ITC: otherItc, 
            OTHER_NON_ITC: otherNonItc
          } = project.budgetCosts || {};
          
          if (project.pricingType === PricingType.ACQUISITION) {
            projectTotal.epcCost = null;
            results.epcCost = null;
          } else {
            projectTotal.epcCost = normalizeToOverall(projectSizeKwdc, { amount: project.epcCost, unit: project.epcCostUnit } as ProjectBid);
            results.epcCost! += projectTotal.epcCost;
          }

          projectTotal.nrsFee = null;
          projectTotal.interconnection = normalizeToOverall(projectSizeKwdc, { amount: interconnection?.amount, unit: interconnection?.unit } as ProjectBid);
          projectTotal.otherItc = normalizeToOverall(projectSizeKwdc, { amount: otherItc?.amount, unit: otherItc?.unit } as ProjectBid);
          projectTotal.otherNonItc = normalizeToOverall(projectSizeKwdc, { amount: otherNonItc?.amount, unit: otherNonItc?.unit } as ProjectBid);
          projectTotal.totalCost = projectTotal.interconnection + projectTotal.otherItc + projectTotal.otherNonItc + (projectTotal.epcCost ?? 0);
          projectTotal.sizeKwdc = projectSizeKwdc;

          results.nrsFee = null;
          results.interconnection += projectTotal.interconnection;
          results.otherItc += projectTotal.otherItc;
          results.otherNonItc += projectTotal.otherNonItc;
          results.totalCost += projectTotal.totalCost;
          results.sizeKwdc += projectTotal.sizeKwdc;
          results.sizeMwdc += projectSizeMwdc;
        } else {

          // Once the project is in diligence (or at any point for investors), projectCosts will always be a single object
          // const projectCostsArray = projectCosts as any[];
          // const projectCostsObj = projectCostsArray[0];
          // const { total, sizeKwdc, epcCostTotal } = projectCostsObj as any;
          
          const { total, sizeKwdc, epcCostTotal } = projectCosts as any;

          projectTotal.epcCost = epcCostTotal;
          projectTotal.interconnection = total.interconnection;
          projectTotal.otherItc = total.otherItc;
          projectTotal.otherNonItc = total.otherNonItc;
          projectTotal.nrsFee = total.feesTotal!;   // Can't be undefined once there is a bid
          projectTotal.totalCost = total.total;
          projectTotal.sizeKwdc = sizeKwdc;
          
          results.epcCost! += projectTotal.epcCost || 0;   // Can't be null once there is a bid
          results.interconnection += projectTotal.interconnection;
          results.otherItc += projectTotal.otherItc;
          results.otherNonItc += projectTotal.otherNonItc;
          results.nrsFee! += projectTotal.nrsFee || 0;     // Can't be null once there is a bid
          results.totalCost += projectTotal.totalCost;
          results.sizeKwdc += projectTotal.sizeKwdc;
        }
        
        results.projectTotals.push(projectTotal);
      }
      
      return results;
    }, [displayBlobs]
  );

  return {
    projectBlobs,
    projectBidCountMap,
    portfolioState,
    totalBid,
    locations,
    portfolioTotals,
    displayBlobs,
    projectCount,
    parentProjects,
    childProjects,
    parentProjectChildProjectMap
  };
};

export const useFetchPortfolioCalculatedValues = (id: any, callback: any) => {
  const {execute: fetchPortfolioData, status: fetchPortfolioStatus, value: portfolio} = usePromiseWatcher(
    async () => id ? fetchPortfolio(id) : null,
    'fetching the portfolio\'s data',
    [id]
  );
  const {
    projectBlobs,
    portfolioState,
    totalBid,
    locations,
    portfolioTotals,
    displayBlobs
  } = usePortfolioCalculatedValues(portfolio, []); //TODO Portfolio typing

  useEffect(
    () => {
      if (!portfolio) {
        return;
      }
      callback({
        portfolio,
        projectBlobs,
        portfolioState,
        totalBid,
        locations,
        portfolioTotals,
        displayBlobs
      });
    },
    [portfolio, projectBlobs]
  );

  return {
    initiateValueCalculation: fetchPortfolioData,
    calculatedValuesStatus: fetchPortfolioStatus
  };
};
